import React, { useEffect, useState, useReducer, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'
import cn from 'classnames'

import { SUPPORT_ARTICLE_URLS } from 'constants'

import { fetchConversionGoals, fetchLeadForms } from 'api/integrations'
import { fetchCustomEventTypes } from 'api/campaigns'
import { initStateArray, arrayReducer, OVERWRITE } from 'reducers/default'
import { useOutsideCheckClick } from 'hooks/UseOnClickOutside'
import { AssetClass, CampaignGoal, Platform } from 'api/models'
import { findByAssetClass } from 'domain/integrationAssets/mappers'
import {
  findIntegrationDetails,
  replaceIntegrationDetailGroup,
  GOOGLE_CONVERSION_ACTION_ASSET_CLASS,
} from 'domain/campaignSetup/mappers'
import { SelectionMode } from 'domain/googleConversionGoals/schemas'

import SearchDropdown from './Edit/SearchDropdown'
import { getErrorText } from './Edit/adAccountHelper'
import { useCampaignSetupDispatch, useCampaignSetup } from './Context/CampaignSetupContext'
import { translateSetupError } from './Context/setupError'
import GoogleConversionGoalPicker from './GoogleConversionGoalPicker'
import {
  BiddingTargetValueField,
  currencyAmountToMicros,
  GoogleBiddingStrategyType,
  isBiddingTargetValueMissing,
  microsToCurrencyAmount,
  percentageToRatio,
  ratioToPercentage,
  resolveGoogleBiddingStrategyDefault,
  resolveHasConversionAction,
  getSuitableBiddingStrategyTypes,
  targetValueFieldForStrategy,
} from './googleBiddingDefaults'

import AccordionCard from 'components/Shared/AccordionCard'
import ErrorMessage from 'components/Shared/ErrorMessage'
import InfoBox from 'components/Shared/InfoBox'
import PlatformLabel from 'components/Shared/PlatformLabel'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'

import { RemoveIcon } from 'images'
import {
  getAvailableInstagramAccounts,
  getAvailablePages,
  getAvailablePixels,
} from './Context/util'
import {
  derivePlatformSelectionFromSetup,
  platformIncludesMeta,
  platformIncludesGoogle,
  DEFAULT_SETUP_PLATFORM,
} from './platform'
import {
  permissionErrorProviderName,
  settingsPlatformsHref,
} from 'domain/platform/permissionErrorTarget'
import {
  deriveCampaignTypeFromSetup,
  GoogleCampaignType,
  toApiCampaignType,
} from '../EventCampaigns/Steps/Setup/Goals/campaignType'
import i18n from 'i18n'

// Vocabulary for the conversions-discovery endpoint's own `campaign_type`
// query param (app/api/v4/integrations/connect/google/assets/assets.py's
// _conversion_goals_parser: "DISPLAY | DEMAND_GEN") — distinct from
// `platform_overrides.google.campaign_type`'s Google-Ads-API-shaped
// 'DISPLAY'/'VIDEO_CONVERSIONS' enum used at publish time, don't conflate
// the two.
const CONVERSIONS_API_CAMPAIGN_TYPE = {
  [GoogleCampaignType.display]: 'DISPLAY',
  [GoogleCampaignType.demandGen]: 'DEMAND_GEN',
}

export function PlatformSelectMenu({
  handleClick,
  integrations,
  viableIntegrations,
  required = false,
  subHeadlineText,
  removeInteg,
  selectedIntegration,
  selectText,
  disabled,
  loadingValue = false,
  accountType,
}) {
  const autoSelected = integrations.length === 1 && required
  const [open, setOpen] = useState(false)
  const { t, i18n } = useTranslation()
  const { setComponentError } = useCampaignSetupDispatch()

  const menuRef = useRef(null)
  const isPending = disabled || loadingValue

  useOutsideCheckClick(menuRef, setOpen, open)

  const handleMenuClick = () => {
    !isPending && setOpen(!open)
  }

  const rawIsSelectedIntegrationsViable = useMemo(() => {
    // An integration is not viable when it is active and valid, but not in the list of viable integrations
    // Viable integrations are those that are with valid connections to other assets (e.g. an instagram account is connected to a facebook page and ad account)
    return (
      !selectedIntegration ||
      (integrations.find((x) => x.asset_id === selectedIntegration.asset_id)?.valid &&
        viableIntegrations?.find((x) => x.asset_id === selectedIntegration.asset_id))
    )
  }, [selectedIntegration, integrations, viableIntegrations])
  const matchedIntegration = selectedIntegration?.asset_id
    ? integrations.find((x) => x.asset_id === selectedIntegration.asset_id)
    : null
  const rawSelectedIntegrationInvalid =
    !!selectedIntegration?.asset_id && matchedIntegration?.valid !== true

  // This menu is rendered once per asset row (Meta profile/ad account/pixel,
  // Google ad account), so the row's own platform decides which vendor the
  // warnings below name and which Settings panel they link to.
  const rowPlatform = matchedIntegration?.platform ?? selectedIntegration?.platform
  const isGoogleRow = rowPlatform === Platform.google
  const providerName = permissionErrorProviderName(rowPlatform)
  const settingsHref = settingsPlatformsHref(rowPlatform)

  // Google-only: invitation_status/billing_valid already fold into `.valid`
  // (see asset_enrichment.py's enrich_google_assets), but identity
  // verification doesn't — an account can be `valid: true` while Google
  // still requires the partner to complete identity verification before the
  // account is actually usable for campaigns.
  const matchedGoogleIntegration = isGoogleRow ? matchedIntegration : null
  const rawSelectedIntegrationPendingVerification =
    matchedGoogleIntegration?.identity_verification_status === 'PENDING_USER_ACTION'

  // `integrations`/`viableIntegrations` are built from partnerAccounts,
  // whose `.valid` starts out as a placeholder (fetchAccountsAsync's
  // `valid: !!is_active`) until the account-defaulting effect's own
  // enrich/validate round trip refines it with the real, backend-checked
  // value (asyncHandlers.js's enrichAccountsAsync → patchAccounts). Reading
  // the three checks above against that placeholder flags a perfectly fine
  // account as invalid for the brief window before that round trip
  // resolves — most visible on Google, whose placeholder (is_active alone)
  // is a weaker proxy than Meta's for the real, fully-enriched validity
  // (invitation_status/billing_valid folded in server-side). Suppress all
  // three while loadingValue signals that round trip hasn't settled yet,
  // rather than flashing a false error.
  const isSelectedIntegrationsViable = loadingValue || rawIsSelectedIntegrationsViable
  const selectedIntegrationInvalid = !loadingValue && rawSelectedIntegrationInvalid
  const selectedIntegrationPendingVerification =
    !loadingValue && rawSelectedIntegrationPendingVerification

  useEffect(() => {
    setComponentError(
      accountType,
      selectedIntegrationInvalid ||
        !isSelectedIntegrationsViable ||
        selectedIntegrationPendingVerification,
    )
    // Clears this field's error when it unmounts — e.g. platform selection
    // switches away from Meta and the Meta-only fields disappear. Without
    // this, a stale `true` here blocks isIntegrationsValid/isStepValid(2)
    // forever, even though the field (and its platform) is no longer part
    // of the setup.
    return () => setComponentError(accountType, false)
  }, [
    accountType,
    selectedIntegrationInvalid,
    isSelectedIntegrationsViable,
    selectedIntegrationPendingVerification,
    setComponentError,
  ])

  const errorKey =
    selectedIntegration?.asset_class === AssetClass.profile &&
    selectedIntegration.platform === Platform.instagram
      ? 'instagramAccountNotViable'
      : 'assetNotViable'
  const supportArticleUrl =
    SUPPORT_ARTICLE_URLS?.meta?.[i18n.language] || SUPPORT_ARTICLE_URLS?.meta?.en

  const hasOptions =
    viableIntegrations.length > 1 ||
    (viableIntegrations.length === 1 && selectedIntegration?.id !== viableIntegrations[0].id)

  return (
    <div className="select-ad-platform_select-menu">
      {!!subHeadlineText && (
        <>
          <strong className="select-ad-platform_select-menu_select-label">{subHeadlineText}</strong>
          {required && <span className="warning-red">*</span>}
        </>
      )}
      <div
        onClick={handleMenuClick}
        ref={menuRef}
        className={cn({ 'select-ad-platform_select-menu_wrapper': true, open })}
        style={loadingValue ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      >
        <div
          className={cn('select-ad-platform_select-menu_input', {
            open,
            selected: autoSelected || selectedIntegration?.id ? 'selected' : '',
            disabled,
            invalid: selectedIntegrationInvalid,
          })}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              <div className="selected-value" style={{ color: '#3b3b3b' }}>
                {loadingValue
                  ? t('common.loading')
                  : selectedIntegration
                    ? `${selectedIntegration.asset_label} (ID: ${selectedIntegration.asset_id})`
                    : selectText}
              </div>
            </div>

            {!required && selectedIntegration?.id && !isPending && (
              <div className="remove">
                <RemoveIcon width={14} onClick={(e) => removeInteg(e, selectedIntegration.id)} />
              </div>
            )}
          </div>
          {!isPending && (
            <div
              style={{
                transitionDuration: '0.2s',
                marginLeft: !autoSelected && viableIntegrations.length > 1 ? 'unset' : 'auto',
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
              }}
              className={cn({ 'events-insights-page__status_menu_arrow': true, open })}
            />
          )}
        </div>
        <div className={cn({ 'select-ad-platform_select-menu_drop-menu_wrapper': true, open })}>
          {/* there are no other choices remaining - show an empty select list */}
          {!hasOptions && (
            <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
              <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>
                {t('common.noMoreFields')}
              </div>
            </div>
          )}

          {/* we have many integration choices available - allow the user to choose from a list */}
          {viableIntegrations &&
            viableIntegrations.length > 0 &&
            viableIntegrations.map((eachIntegration, index) => {
              if (selectedIntegration?.id === eachIntegration.id) return null
              return (
                <div
                  className={cn('select-ad-platform_select-menu_drop-menu_row_wrapper', {
                    invalid: !eachIntegration.valid,
                  })}
                  key={index}
                  onClick={(e) => {
                    if (!eachIntegration.valid) return
                    handleClick(e, eachIntegration.id)
                    setOpen(false)
                  }}
                >
                  <div>
                    {eachIntegration.asset_label} (ID: {eachIntegration.asset_id})
                  </div>
                </div>
              )
            })}
        </div>
      </div>
      {selectedIntegrationInvalid && (
        <p className="warning-red mt-2">
          <Trans
            i18nKey="Recommendations.campaign.invalidAccount"
            components={{
              a: (
                <a
                  href={settingsHref}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="d-inline-block"
                  style={{ color: '#f56666', textDecoration: 'underline' }}
                >
                  {providerName} assets page
                </a>
              ),
            }}
            values={{
              accountName: selectedIntegration.asset_label,
              accountId: selectedIntegration.asset_id,
              provider: providerName,
            }}
          >
            Permission missing to access {selectedIntegration.asset_label} (ID:{' '}
            {selectedIntegration.asset_id}). To enable it for use, please reconnect the asset on{' '}
            <a href={settingsHref}>{providerName} asset page</a> and grant us the necessary access.
          </Trans>
        </p>
      )}
      {!isSelectedIntegrationsViable &&
        (isGoogleRow ? (
          <p className="warning-red mt-2">
            <Trans
              i18nKey="Recommendations.campaign.googleAccountNotViable"
              components={{
                a: (
                  <a
                    href={settingsHref}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="d-inline-block"
                    style={{ color: '#f56666', textDecoration: 'underline' }}
                  >
                    Google assets page
                  </a>
                ),
              }}
              values={{
                accountName: selectedIntegration.asset_label,
                accountId: selectedIntegration.asset_id,
              }}
            >
              Selected Google Ads account {selectedIntegration.asset_label} (ID:{' '}
              {selectedIntegration.asset_id}) can&apos;t be used for campaigns yet. Please check the
              account&apos;s connection and billing setup on the{' '}
              <a href={settingsHref}>Google assets page</a> and grant us the necessary access.
            </Trans>
          </p>
        ) : (
          <p className="warning-red mt-2">
            <Trans
              i18nKey={`Recommendations.campaign.${errorKey}`}
              components={[
                <a
                  href={settingsHref}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="d-inline-block"
                  style={{ color: '#f56666', textDecoration: 'underline' }}
                  key={0}
                >
                  Meta assets page
                </a>,
                <a
                  href={supportArticleUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="d-inline-block"
                  style={{ color: '#f56666', textDecoration: 'underline' }}
                  key={1}
                >
                  Meta support article
                </a>,
              ]}
              values={{
                accountName: selectedIntegration.asset_label,
                accountId: selectedIntegration.asset_id,
              }}
            >
              Selected Ad account is not connected to {selectedIntegration.asset_label} (ID:{' '}
              {selectedIntegration.asset_id}). To enable it for use, please link the accounts on
              Meta and then reconnect the asset on <a href={settingsHref}>Meta asset page</a> to
              grant us the necessary access. You can refer to the{' '}
              <a href={supportArticleUrl}>Meta support article</a> for guidance on connecting the
              missing permission.
            </Trans>
          </p>
        ))}
      {selectedIntegrationPendingVerification && (
        <p className="warning-red mt-2">
          <Trans
            i18nKey="Recommendations.campaign.googleIdentityVerificationPending"
            components={{
              a: (
                <a
                  href={matchedGoogleIntegration.identity_verification_action_url}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="d-inline-block"
                  style={{ color: '#f56666', textDecoration: 'underline' }}
                >
                  Verify your identity
                </a>
              ),
            }}
            values={{
              accountName: selectedIntegration.asset_label,
              accountId: selectedIntegration.asset_id,
            }}
          >
            Identity verification required to use {selectedIntegration.asset_label} (ID:{' '}
            {selectedIntegration.asset_id}). Please{' '}
            <a href={matchedGoogleIntegration.identity_verification_action_url}>
              verify your identity
            </a>{' '}
            with Google to enable it for use.
          </Trans>
        </p>
      )}
    </div>
  )
}

PlatformSelectMenu.propTypes = {
  handleClick: PropTypes.func.isRequired,
  integrations: PropTypes.array.isRequired,
  viableIntegrations: PropTypes.array,
  required: PropTypes.bool,
  subHeadlineText: PropTypes.string,
  removeInteg: PropTypes.func,
  selectedIntegration: PropTypes.object,
  selectText: PropTypes.string,
  disabled: PropTypes.bool,
  loadingValue: PropTypes.bool,
  accountType: PropTypes.string,
}

// A single reusable numeric input for whichever target value the currently
// selected bidding strategy needs — a Viewable CPM bid (currency amount) or
// a Target ROAS (percentage). `value`/`onChange` are always in the
// user-facing display unit; the caller converts to/from the wire unit
// (micros / ratio) at the save boundary, see googleBiddingDefaults.js's
// currencyAmountToMicros/percentageToRatio. Debounced like RadiusInput.jsx
// so each keystroke doesn't fire a save request.
function BiddingTargetValueInput({ value, onChange, disabled, placeholder, invalid, suffix }) {
  const [text, setText] = useState(value != null ? String(value) : '')
  const timeoutRef = useRef()

  useEffect(() => {
    setText(value != null ? String(value) : '')
  }, [value])

  const handleChange = (e) => {
    const raw = e.target.value
    setText(raw)
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (raw === '') {
        onChange(null)
        return
      }
      const parsed = parseFloat(raw)
      if (!Number.isNaN(parsed)) onChange(parsed)
    }, 500)
  }

  return (
    <div className={cn('bidding-target-value', { 'has-suffix': !!suffix })}>
      <input
        className={cn('form-control', { invalid })}
        type="number"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        disabled={disabled}
      />
      {/* Unit marker sits inside the field rather than only in the label, so
          the value on screen reads as what it is — a percentage, not the
          ratio stored on the wire. Hidden while empty so it doesn't cover
          the placeholder. */}
      {!!suffix && !!text && <span className="bidding-target-value__suffix">{suffix}</span>}
    </div>
  )
}

BiddingTargetValueInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
  suffix: PropTypes.string,
}

function integrationSelectionKey(items) {
  // Can't use `.id` because conversion/lead_form entries may not have one.
  return items
    .map((x) => `${x.asset_id}|${x.asset_class}|${x.platform}`)
    .sort()
    .join(',')
}

const SelectAdPlatformForm = ({ isDisabled }) => {
  const { t } = useTranslation()
  const {
    setup,
    accounts: partnerAccounts,
    accountsLoaded,
    metaConnectionStatus,
    metaStatusLoaded,
    canAnonEdit,
  } = useCampaignSetup()
  const effectivelyDisabled = isDisabled || !canAnonEdit
  const platform = derivePlatformSelectionFromSetup(setup)
  const googleConversionsCampaignType =
    CONVERSIONS_API_CAMPAIGN_TYPE[deriveCampaignTypeFromSetup(setup)]
  const showMeta = platformIncludesMeta(platform)
  const showGoogle = platformIncludesGoogle(platform)
  const {
    integration_details: currentIntegrations,
    goal,
    conversion_id,
    lead_form_id,
    custom_event_type,
  } = setup
  const { enrichAccountsAndReconfigure, enrichAccounts, setComponentError } =
    useCampaignSetupDispatch()

  // A rejected selection save (e.g. ad accounts whose timezones differ) is kept here until the
  // selection changes: state.error is cleared by any later request, including the background
  // default-writing ones this form makes as soon as an account is picked.
  const [selectionError, setSelectionError] = useState(null)
  const selectionSaveRef = useRef(0)

  const saveSelection = useCallback(
    (config, callback = () => {}) => {
      const save = ++selectionSaveRef.current
      setSelectionError(null)
      enrichAccountsAndReconfigure(config, (result) => {
        if (save === selectionSaveRef.current) {
          setSelectionError(result?.success === false ? (result.error ?? null) : null)
        }
        callback(result)
      })
    },
    [enrichAccountsAndReconfigure],
  )

  // Blocks Next while the stored selection is not what the form shows.
  useEffect(() => {
    setComponentError('accountSelection', selectionError)
    return () => setComponentError('accountSelection', null)
  }, [selectionError, setComponentError])

  const [selectedIntegrations, dispatchSelectedIntegrationsAction] = useReducer(arrayReducer, {
    ...initStateArray,
    items: currentIntegrations,
  })
  const lastValidatedSelectionRef = useRef(null)
  // tracks the last selection that was automatically applied, preventing redundant network calls.
  const lastAutoAppliedSelectionRef = useRef(null)
  // Whether the account-defaulting effect below has completed its initial
  // pass — either it persisted/validated a selection and got a response
  // back, or it determined on entry there was nothing to do (disabled,
  // anon without edit rights, or genuinely no selection to validate). Feeds
  // PlatformSelectMenu's loadingValue so fields don't flash invalid against
  // partnerAccounts's placeholder `valid` (see that effect's own comments)
  // before this settles.
  const [initialAccountsValidated, setInitialAccountsValidated] = useState(false)
  const [conversionGoals, dispatchConversionGoalsAction] = useReducer(arrayReducer, initStateArray)
  const [leadForms, dispatchLeadFormsAction] = useReducer(arrayReducer, initStateArray)
  const [customEventTypes, dispatchCustomEventTypesAction] = useReducer(
    arrayReducer,
    initStateArray,
  )
  const leadFormValue = useMemo(() => {
    return leadForms.items.find((el) => el.id === lead_form_id)
  }, [lead_form_id, leadForms.items])

  // Meta-scoped: drives fetchConversionGoals/fetchLeadForms (Meta-only calls)
  // and getAvailablePages/getAvailablePixels/getAvailableInstagramAccounts —
  // must not resolve to a Google ad account once both platforms' rows share
  // partnerAccounts/selectedIntegrations.
  const selectedAdAccountId = useMemo(() => {
    return findByAssetClass(selectedIntegrations.items, {
      platform: Platform.facebook,
      assetClass: AssetClass.ad_account,
    })?.asset_id
  }, [selectedIntegrations])
  const selectedPageId = useMemo(() => {
    return selectedIntegrations.items.find(
      (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
    )?.asset_id
  }, [selectedIntegrations])

  useEffect(() => {
    // Meta-only endpoint (no Google equivalent) — skip the fetch entirely
    // when Meta isn't among the selected platform(s).
    if (!showMeta) return
    const hasPixel = (selectedIntegrations.items || []).some(
      (el) => el.asset_class === AssetClass.pixel,
    )
    fetchCustomEventTypes(dispatchCustomEventTypesAction, goal, hasPixel, lead_form_id)
  }, [goal, lead_form_id, showMeta, selectedIntegrations.items])

  const retrieveConversionGoals = useCallback(() => {
    if (selectedAdAccountId)
      fetchConversionGoals(dispatchConversionGoalsAction, selectedAdAccountId, lead_form_id)
  }, [selectedAdAccountId, lead_form_id])

  const retrieveLeadForms = useCallback(() => {
    if (goal === CampaignGoal.utilization && selectedPageId && selectedAdAccountId)
      fetchLeadForms(dispatchLeadFormsAction, selectedPageId, selectedAdAccountId)
  }, [selectedPageId, selectedAdAccountId, goal])

  useEffect(() => {
    retrieveConversionGoals()
    retrieveLeadForms()
  }, [retrieveConversionGoals, retrieveLeadForms])

  useEffect(() => {
    // Skip for anon users without edit rights (reconfigure would 403) and for
    // published campaigns (effectivelyDisabled), where auto-swapping to a new
    // default would be a silent data change. Bail out before reading
    // partnerAccounts or computing any default.
    if (!canAnonEdit || effectivelyDisabled) {
      setInitialAccountsValidated(true)
      return
    }

    // partnerAccounts/metaConnectionStatus start out empty/null before their
    // own fetches resolve — computing defaults against that incomplete data
    // picks a first-pass selection (e.g. skipping Meta relationship
    // validation because a not-yet-loaded metaConnectionStatus looks
    // identical to a genuinely disconnected one), persists it, then
    // recomputes and persists again once the real data arrives. Wait for
    // both before running at all; metaConnectionStatus only matters when
    // Meta is actually part of the platform selection.
    if (!accountsLoaded) return
    if (platformIncludesMeta(platform) && !metaStatusLoaded) return

    // select a default ad account and profiles
    const { adAccount, page, instagramAccount, pixel, googleAdAccount } = getDefaultIntegrations(
      partnerAccounts,
      selectedIntegrations.items,
      metaConnectionStatus,
      platform,
    )
    const newIntegrations = [adAccount, page, instagramAccount, pixel, googleAdAccount].filter(
      (x) => x,
    )

    const hasNewSelections =
      newIntegrations.length &&
      // check if the new integrations are different from the current integrations
      !newIntegrations
        .map((x) => x.id)
        .every((pid) => selectedIntegrations.items.map((x) => x.id).includes(pid))

    if (hasNewSelections) {
      // Guard against unstable round-trips — enrichAccountsAndReconfigure changes
      // both `partnerAccounts` and `setup` (dependencies of this effect).
      const newSelectionIds = integrationSelectionKey(newIntegrations)
      if (lastAutoAppliedSelectionRef.current === newSelectionIds) return
      lastAutoAppliedSelectionRef.current = newSelectionIds
      // enrichAccountsAndReconfigure below already enriches these exact
      // accounts as part of its own persist call — mark them validated too,
      // so the validate-only branch below doesn't immediately re-enrich the
      // same selection again once partnerAccounts updates from this call's
      // own patchAccounts dispatch (a 3rd, redundant request on top of the
      // 2 this persist call already makes).
      lastValidatedSelectionRef.current = newSelectionIds

      dispatchSelectedIntegrationsAction({ type: OVERWRITE, payload: newIntegrations })
      saveSelection({ ...setup, integration_details: newIntegrations }, () =>
        setInitialAccountsValidated(true),
      )
      return
    }

    const selectedIds = integrationSelectionKey(selectedIntegrations.items)
    if (selectedIds && lastValidatedSelectionRef.current !== selectedIds) {
      lastValidatedSelectionRef.current = selectedIds
      enrichAccounts(selectedIntegrations.items, () => setInitialAccountsValidated(true))
      return
    }

    // selectedIds matching the ref means a validation for this exact
    // selection is either already in flight (kicked off by this same effect
    // moments ago, its own local dispatchSelectedIntegrationsAction already
    // landed a render before its network round trip has) or already
    // resolved — either way its own callback owns setInitialAccountsValidated,
    // so don't touch it here; doing so unconditionally raced that in-flight
    // case and marked validation "done" before partnerAccounts actually had
    // the real (non-placeholder) `valid`. Only genuinely nothing-to-validate
    // (no selection at all) is safe to mark done right here.
    if (!selectedIds) {
      setInitialAccountsValidated(true)
    }
  }, [
    partnerAccounts,
    accountsLoaded,
    setup,
    enrichAccountsAndReconfigure,
    enrichAccounts,
    selectedIntegrations.items,
    metaConnectionStatus,
    metaStatusLoaded,
    canAnonEdit,
    effectivelyDisabled,
    platform,
  ])

  const onIntegrationClicked = (id) => {
    const integration = partnerAccounts.find((x) => x.id === id)
    if (!integration?.asset_class)
      throw new Error(
        `Selected integration id was invalid or, or the corresponding integration does not have a valid asset_class: ${id}`,
      )

    let newIntegrations = []

    if (!metaConnectionStatus) {
      // Partner is not connected to Meta, so we don't enforce asset relationship validation.
      newIntegrations = [
        ...selectedIntegrations.items.filter(
          (x) => x.asset_class !== integration.asset_class || x.platform !== integration.platform,
        ),
        integration,
      ]
    } else {
      newIntegrations = updateSelectedIntegrations(
        integration,
        partnerAccounts,
        selectedIntegrations.items,
        metaConnectionStatus,
      )
    }

    // enrichAccountsAndReconfigure below enriches these exact accounts as
    // part of its own persist call — pre-mark them validated so the
    // validate-only effect branch doesn't immediately re-enrich the same
    // selection again once partnerAccounts updates from this call's own
    // patchAccounts dispatch.
    lastValidatedSelectionRef.current = integrationSelectionKey(newIntegrations)
    dispatchSelectedIntegrationsAction({ type: OVERWRITE, payload: newIntegrations })
    saveSelection({ ...setup, integration_details: newIntegrations })
  }

  const onIntegrationRemoved = (e, id) => {
    e.preventDefault()
    e.stopPropagation()

    const newIntegrations = selectedIntegrations.items.filter((x) => x.id !== id)

    lastValidatedSelectionRef.current = integrationSelectionKey(newIntegrations)
    dispatchSelectedIntegrationsAction({ type: OVERWRITE, payload: newIntegrations })
    saveSelection({ ...setup, integration_details: newIntegrations })
  }

  const isPixel =
    (selectedIntegrations.items || []).some((el) => el.asset_class === AssetClass.pixel) &&
    goal !== CampaignGoal.visibility

  // Same gap as isSavingBiddingStrategy above — conversionValue/leadFormValue
  // are derived straight from setup.conversion_id/custom_event_type/
  // lead_form_id (server-confirmed state), with no local optimistic echo, so
  // these two fields need their own pending flags to show a save is in
  // flight rather than just sitting there looking interactive. One shared
  // flag for conversion goal + custom event type since they're the same
  // dropdown.
  const [isSavingConversionGoal, setIsSavingConversionGoal] = useState(false)
  const [isSavingLeadForm, setIsSavingLeadForm] = useState(false)

  const onConversionGoalChanged = useCallback(
    (conversionId) => {
      const config = {
        ...setup,
        conversion_id: conversionId,
      }
      setIsSavingConversionGoal(true)
      enrichAccountsAndReconfigure(
        platformIncludesMeta
          ? { ...config, custom_event_type: getDefaultCustomEventType(lead_form_id) }
          : config,
        () => setIsSavingConversionGoal(false),
      )
    },
    [setup, enrichAccountsAndReconfigure, lead_form_id],
  )

  const onCustomEventTypeChanged = useCallback(
    (customEventType) => {
      setIsSavingConversionGoal(true)
      enrichAccountsAndReconfigure(
        {
          ...setup,
          custom_event_type: customEventType,
          conversion_id: '',
        },
        () => setIsSavingConversionGoal(false),
      )
    },
    [setup, enrichAccountsAndReconfigure],
  )

  const onLeadFormChanged = useCallback(
    (leadFormId) => {
      setIsSavingLeadForm(true)
      enrichAccountsAndReconfigure(
        {
          ...setup,
          lead_form_id: leadFormId,
          // reset conversion_id and custom_event_type when lead form changes
          // as new conversion goals and custom event types will be fetched based on the new lead form
          conversion_id: '',
          custom_event_type: getDefaultCustomEventType(lead_form_id),
        },
        () => setIsSavingLeadForm(false),
      )
    },
    [setup, enrichAccountsAndReconfigure, lead_form_id],
  )

  const googleConversionGoalEntries = useMemo(
    () =>
      findIntegrationDetails(selectedIntegrations.items, {
        platform: Platform.google,
        assetClass: GOOGLE_CONVERSION_ACTION_ASSET_CLASS,
      }),
    [selectedIntegrations],
  )
  const googleConversionGoalItemIds = useMemo(
    () => googleConversionGoalEntries.map((x) => x.asset_id),
    [googleConversionGoalEntries],
  )

  // Google's conversion-goal selection isn't a bare scalar like Meta's (a
  // "category" selection is N sibling conversion_action entries, one per
  // selected goal; a "custom_bundle" selection is a single entry keyed by
  // resource_name rather than asset_id) — folded straight into
  // integration_details via replaceIntegrationDetailGroup instead of
  // resolveConversionTrackingFields's Meta-only scalar plumbing.
  const onGoogleConversionGoalChanged = useCallback(
    ({ mode, items }) => {
      const entries =
        mode === SelectionMode.custom_bundle
          ? items.slice(0, 1).map((item) => ({
              platform: Platform.google,
              asset_class: GOOGLE_CONVERSION_ACTION_ASSET_CLASS,
              type: 'bundle',
              resource_name: item.resource_name,
              asset_id: item.id,
              asset_label: item.name,
              exclusive: true,
            }))
          : items.map((item) => ({
              platform: Platform.google,
              asset_class: GOOGLE_CONVERSION_ACTION_ASSET_CLASS,
              type: 'action',
              asset_id: item.id,
              asset_label: item.name,
            }))

      const newIntegrations = replaceIntegrationDetailGroup(
        selectedIntegrations.items,
        { platform: Platform.google, assetClass: GOOGLE_CONVERSION_ACTION_ASSET_CLASS },
        entries,
      )

      lastValidatedSelectionRef.current = integrationSelectionKey(newIntegrations)
      dispatchSelectedIntegrationsAction({ type: OVERWRITE, payload: newIntegrations })
      saveSelection({ ...setup, integration_details: newIntegrations })
    },
    [setup, enrichAccountsAndReconfigure, selectedIntegrations.items],
  )

  const selectedGoogleAdAccount = useMemo(
    () =>
      findByAssetClass(selectedIntegrations.items, {
        platform: Platform.google,
        assetClass: AssetClass.ad_account,
      }),
    [selectedIntegrations],
  )

  // Whether a Google ad account is either already selected or resolvable
  // from partnerAccounts — mirrors getDefaultIntegrations'/the
  // account-defaulting effect's own resolution exactly, and deliberately
  // reads partnerAccounts (not just selectedGoogleAdAccount above) so it
  // doesn't lag a render behind that effect's own dispatch — see the
  // bidding-strategy-default effect below for why that lag matters.
  const willHaveGoogleAdAccount = useMemo(
    () => !!getDefaultGoogleAdAccount(partnerAccounts, selectedIntegrations.items, platform),
    [partnerAccounts, selectedIntegrations, platform],
  )

  // Surfaced by GoogleConversionGoalPicker from its own discovery fetch —
  // what the ad account's own default biddable conversion goals optimize
  // for (purchase/lead/other), or null if the account has no biddable
  // default at all. Independent of any explicit per-campaign selection.
  const [googleAccountDefaultOrientation, setGoogleAccountDefaultOrientation] = useState(null)
  // Whether GoogleConversionGoalPicker's own discovery fetch has settled at
  // least once for the currently selected Google ad account — distinct from
  // `googleAccountDefaultOrientation` itself, since `null` is both the
  // "not yet known" and the genuine "no biddable default" answer. Feeds the
  // bidding-strategy-default effect below so it doesn't compute (and
  // persist) a default before it actually knows the account's orientation.
  const [googleMetaLoaded, setGoogleMetaLoaded] = useState(false)
  const onGoogleConversionMetaChanged = useCallback((meta) => {
    setGoogleAccountDefaultOrientation(meta?.account_default_orientation ?? null)
    setGoogleMetaLoaded(true)
  }, [])
  useEffect(() => {
    setGoogleMetaLoaded(false)
  }, [selectedGoogleAdAccount?.asset_id])

  // An explicit pick (googleConversionGoalEntries) always counts; absent
  // one, a biddable account default whose orientation matches the current
  // goal counts too — the backend already defers to it at publish time
  // (_apply_conversion_goals no-ops with no explicit conversion_action_id).
  // Feeds the Bidding Strategy default matrix's has_conversion_action param.
  const hasConversionAction = resolveHasConversionAction({
    explicitSelectionCount: googleConversionGoalEntries.length,
    accountDefaultOrientation: googleAccountDefaultOrientation,
    campaignGoal: goal,
  })

  const apiCampaignType = useMemo(
    () => toApiCampaignType(deriveCampaignTypeFromSetup(setup)),
    [setup],
  )

  // See mapCampaignGoalToGoogleObjective's own doc comment in
  // googleBiddingDefaults.js — this VISIBILITY/UTILIZATION mapping (and this
  // lead_form_id reuse in particular, since no Google-specific lead-form
  // concept exists in this UI) is unconfirmed with Kamila/backend.
  const hasLeadForm = !!lead_form_id

  const computedDefaultBiddingStrategy = useMemo(
    () =>
      resolveGoogleBiddingStrategyDefault({
        campaignGoal: goal,
        apiCampaignType,
        hasConversionAction,
        hasLeadForm,
      }),
    [goal, apiCampaignType, hasConversionAction, hasLeadForm],
  )

  // `bidding_strategy` is either the plain string shorthand, or a rich
  // object (`{type, target_roas, cpc_bid_ceiling_micros, ...}`) — only the
  // latter's `type` matters for the dropdown/gating; `target_roas` /
  // `cpc_bid_ceiling_micros` (when present) feed the target-value input
  // alongside `cpm_bid_micros`.
  const persistedBiddingStrategyRaw = setup.platform_overrides?.google?.bidding_strategy
  const persistedBiddingStrategyType =
    persistedBiddingStrategyRaw && typeof persistedBiddingStrategyRaw === 'object'
      ? persistedBiddingStrategyRaw.type
      : persistedBiddingStrategyRaw
  const persistedTargetRoas =
    persistedBiddingStrategyRaw && typeof persistedBiddingStrategyRaw === 'object'
      ? persistedBiddingStrategyRaw.target_roas
      : undefined
  const persistedCpcBidCeilingMicros =
    persistedBiddingStrategyRaw && typeof persistedBiddingStrategyRaw === 'object'
      ? persistedBiddingStrategyRaw.cpc_bid_ceiling_micros
      : undefined
  const persistedCpmBidMicros = setup.platform_overrides?.google?.cpm_bid_micros

  // Once the user picks a strategy themselves, or a resumed setup already
  // has one saved, stop recomputing the default over it — acceptance
  // criteria: resuming restores the saved selection, and a manual override
  // must survive later has_conversion_action/goal changes.
  const [biddingStrategyLocked, setBiddingStrategyLocked] = useState(
    () => !!persistedBiddingStrategyType,
  )

  const effectiveBiddingStrategy = persistedBiddingStrategyType || computedDefaultBiddingStrategy
  const targetValueField = targetValueFieldForStrategy(effectiveBiddingStrategy)

  // Which strategies make sense to offer for the current goal/campaign-type/
  // has_conversion_action — conversion- and value-optimizing strategies
  // aren't suitable for every goal (e.g. MAXIMIZE_CONVERSIONS only fits
  // UTILIZATION, TARGET_ROAS only fits ROAS), per direct product feedback.
  const suitableBiddingStrategyTypes = useMemo(
    () =>
      getSuitableBiddingStrategyTypes({ campaignGoal: goal, apiCampaignType, hasConversionAction }),
    [goal, apiCampaignType, hasConversionAction],
  )
  const isPersistedBiddingStrategySuitable =
    !persistedBiddingStrategyType ||
    suitableBiddingStrategyTypes.includes(persistedBiddingStrategyType)

  useEffect(() => {
    // Published campaign freeze: once effectivelyDisabled, don't auto-persist a bidding strategy.
    if (!showGoogle || !canAnonEdit || effectivelyDisabled) return
    // computedDefaultBiddingStrategy depends on hasConversionAction, which
    // depends on googleAccountDefaultOrientation — computing (and
    // persisting) a default before GoogleConversionGoalPicker's own
    // discovery fetch has settled uses a not-yet-known orientation, then
    // recomputes and persists again once the real orientation arrives. Only
    // wait when there's actually an account to wait on (willHaveGoogleAdAccount,
    // see its own comment above for why it — not selectedGoogleAdAccount —
    // is the right thing to check here) — with none resolvable at all,
    // googleAccountDefaultOrientation's null default is already the
    // correct, stable answer (nothing to discover), and gating on it too
    // would permanently freeze this field for a setup with no Google ad
    // account.
    if (!accountsLoaded) return
    if (willHaveGoogleAdAccount && !googleMetaLoaded) return
    // A locked (manually-picked or resumed) selection is normally left
    // alone — but if the goal/campaign-type changed underneath it and it's
    // no longer a suitable choice (e.g. goal switched away from UTILIZATION
    // while MAXIMIZE_CONVERSIONS was selected), fall through and recompute
    // rather than leave an unsuitable strategy saved.
    if (biddingStrategyLocked && isPersistedBiddingStrategySuitable) return
    if (!computedDefaultBiddingStrategy) return
    if (persistedBiddingStrategyType === computedDefaultBiddingStrategy) return

    enrichAccountsAndReconfigure({
      ...setup,
      platform_overrides: {
        ...setup.platform_overrides,
        google: {
          ...setup.platform_overrides?.google,
          bidding_strategy: computedDefaultBiddingStrategy,
        },
      },
    })
  }, [
    showGoogle,
    canAnonEdit,
    effectivelyDisabled,
    accountsLoaded,
    willHaveGoogleAdAccount,
    googleMetaLoaded,
    biddingStrategyLocked,
    isPersistedBiddingStrategySuitable,
    computedDefaultBiddingStrategy,
    persistedBiddingStrategyType,
    setup,
    enrichAccountsAndReconfigure,
  ])

  // Neither this field nor the auto-default effect above has any local
  // optimistic state — the dropdown's displayed value is derived straight
  // from `persistedBiddingStrategyType` (setup, i.e. server-confirmed
  // state), so without this the field just sits there looking interactive
  // for the ~1-2s enrichAccountsAndReconfigure round trip takes, with no
  // indication a save is actually in flight.
  const [isSavingBiddingStrategy, setIsSavingBiddingStrategy] = useState(false)

  // computedDefaultBiddingStrategy (and so targetValueField) is unstable
  // while hasConversionAction is still resting on googleAccountDefaultOrientation's
  // not-yet-known null default — same window the dropdown's own loadingValue
  // already covers. Reused below to keep the target-value input from
  // flashing between cpm/cpc-ceiling/target-roas shapes as that settles.
  const isBiddingStrategyLoading =
    (willHaveGoogleAdAccount && !googleMetaLoaded) || isSavingBiddingStrategy

  const isBiddingStrategySettled = showGoogle && accountsLoaded && !isBiddingStrategyLoading
  const isBiddingStrategyMissing =
    isBiddingStrategySettled && !effectivelyDisabled && !effectiveBiddingStrategy
  const isTargetRoasMissing =
    isBiddingStrategySettled &&
    !effectivelyDisabled &&
    isBiddingTargetValueMissing({
      biddingStrategyType: effectiveBiddingStrategy,
      targetRoas: persistedTargetRoas,
    })

  useEffect(() => {
    // Feeds isIntegrationsValid → isStepValid(2..4)
    setComponentError('googleBiddingStrategy', isBiddingStrategyMissing || isTargetRoasMissing)
    return () => setComponentError('googleBiddingStrategy', false)
  }, [isBiddingStrategyMissing, isTargetRoasMissing, setComponentError])

  const onBiddingStrategyChanged = useCallback(
    (type) => {
      setBiddingStrategyLocked(true)
      // unset target value when null
      const biddingStrategy =
        type === GoogleBiddingStrategyType.targetRoas && persistedTargetRoas != null
          ? { type, target_roas: persistedTargetRoas }
          : type
      const nextGoogleOverrides = {
        ...setup.platform_overrides?.google,
        bidding_strategy: biddingStrategy,
      }
      if (type !== GoogleBiddingStrategyType.manualCpm) {
        delete nextGoogleOverrides.cpm_bid_micros
      }
      setIsSavingBiddingStrategy(true)
      enrichAccountsAndReconfigure(
        {
          ...setup,
          platform_overrides: { ...setup.platform_overrides, google: nextGoogleOverrides },
        },
        () => setIsSavingBiddingStrategy(false),
      )
    },
    [setup, enrichAccountsAndReconfigure, persistedTargetRoas],
  )

  const targetValueDisplayValue = useMemo(() => {
    if (targetValueField === BiddingTargetValueField.cpmBidMicros) {
      return microsToCurrencyAmount(persistedCpmBidMicros)
    }
    if (targetValueField === BiddingTargetValueField.targetRoas) {
      return ratioToPercentage(persistedTargetRoas)
    }
    if (targetValueField === BiddingTargetValueField.cpcBidCeilingMicros) {
      return microsToCurrencyAmount(persistedCpcBidCeilingMicros)
    }
    return null
  }, [targetValueField, persistedCpmBidMicros, persistedTargetRoas, persistedCpcBidCeilingMicros])

  const onTargetValueChanged = useCallback(
    (displayValue) => {
      if (targetValueField === BiddingTargetValueField.cpmBidMicros) {
        enrichAccountsAndReconfigure({
          ...setup,
          platform_overrides: {
            ...setup.platform_overrides,
            google: {
              ...setup.platform_overrides?.google,
              cpm_bid_micros: currencyAmountToMicros(displayValue),
            },
          },
        })
      } else if (targetValueField === BiddingTargetValueField.targetRoas) {
        const ratio = percentageToRatio(displayValue)
        enrichAccountsAndReconfigure({
          ...setup,
          platform_overrides: {
            ...setup.platform_overrides,
            google: {
              ...setup.platform_overrides?.google,
              bidding_strategy:
                ratio == null
                  ? GoogleBiddingStrategyType.targetRoas
                  : { type: GoogleBiddingStrategyType.targetRoas, target_roas: ratio },
            },
          },
        })
      } else if (targetValueField === BiddingTargetValueField.cpcBidCeilingMicros) {
        // Optional, unlike target_roas — clearing it drops back to the plain
        // string shorthand rather than writing the key as null, which the
        // backend's integer-typed schema rejects.
        const micros = currencyAmountToMicros(displayValue)
        enrichAccountsAndReconfigure({
          ...setup,
          platform_overrides: {
            ...setup.platform_overrides,
            google: {
              ...setup.platform_overrides?.google,
              bidding_strategy:
                micros == null
                  ? effectiveBiddingStrategy
                  : { type: effectiveBiddingStrategy, cpc_bid_ceiling_micros: micros },
            },
          },
        })
      }
    },
    [targetValueField, effectiveBiddingStrategy, setup, enrichAccountsAndReconfigure],
  )

  const biddingStrategyOptions = useMemo(
    () =>
      suitableBiddingStrategyTypes.map((type) => ({
        id: type,
        name: t(`Recommendations.campaign.googleBiddingStrategy.types.${type}`),
      })),
    [suitableBiddingStrategyTypes, t],
  )

  const biddingStrategyValue = useMemo(
    () => biddingStrategyOptions.find((x) => x.id === effectiveBiddingStrategy) ?? null,
    [biddingStrategyOptions, effectiveBiddingStrategy],
  )

  const conversionValue = useMemo(() => {
    const conversion = conversionGoals.items.find((el) => el.id === conversion_id)
    if (conversion) return conversion
    if (!custom_event_type) return null

    return {
      id: custom_event_type,
      name: t(`Recommendations.campaign.customEventTypes.${custom_event_type}`),
      type: 'custom_event',
    }
  }, [conversion_id, custom_event_type, conversionGoals.items, t])

  const conversionOptions = useMemo(() => {
    return customEventTypes.items
      .map((x) => ({
        id: x,
        name: t(`Recommendations.campaign.customEventTypes.${x}`),
        type: 'custom_event',
        default: x === (lead_form_id ? 'LEAD' : 'PURCHASE'),
      }))
      .concat(conversionGoals.items)
  }, [conversionGoals.items, customEventTypes.items, t, lead_form_id])

  const selectedAdAccount = useMemo(
    () =>
      findByAssetClass(selectedIntegrations.items, {
        platform: Platform.facebook,
        assetClass: AssetClass.ad_account,
      }),
    [selectedIntegrations],
  )

  const selectedPage = useMemo(
    () =>
      selectedIntegrations.items.find(
        (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
      ),
    [selectedIntegrations],
  )

  const leadSupportArticleUrl =
    SUPPORT_ARTICLE_URLS?.leadForms?.[i18n.language] || SUPPORT_ARTICLE_URLS?.leadForms?.en

  return (
    <div>
      {!accountsLoaded ? (
        <LoadingSpinner size={SpinnerSize.LARGE} />
      ) : (
        <div className="select-ad-platform">
          <form className="select-ad-platform_wrapper">
            {partnerAccounts.error && <p className="warning-red">{partnerAccounts.message}</p>}
            {showMeta && (
              <AccordionCard
                title={t('Recommendations.socialPublishAccountTitle')}
                wrapperClassName="select-ad-platform_section"
              >
                <PlatformSelectMenu
                  handleClick={(_, id) => onIntegrationClicked(id)}
                  removeInteg={(e, id) => onIntegrationRemoved(e, id)}
                  integrations={partnerAccounts.filter(
                    (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
                  )}
                  viableIntegrations={getAvailablePages(
                    partnerAccounts,
                    selectedAdAccount,
                    metaConnectionStatus,
                  )}
                  subHeadlineText={t('Recommendations.facebookPage')}
                  selectedIntegration={selectedIntegrations.items.find(
                    (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
                  )}
                  selectText={t('Recommendations.campaign.selectFacebookPage')}
                  disabled={effectivelyDisabled}
                  required
                  accountType="facebook_profile"
                />

                <PlatformSelectMenu
                  handleClick={(_, id) => onIntegrationClicked(id)}
                  removeInteg={(e, id) => onIntegrationRemoved(e, id)}
                  integrations={partnerAccounts.filter(
                    (x) =>
                      x.asset_class === AssetClass.profile && x.platform === Platform.instagram,
                  )}
                  viableIntegrations={getAvailableInstagramAccounts(
                    partnerAccounts,
                    selectedAdAccount,
                    selectedPage,
                    metaConnectionStatus,
                  )}
                  subHeadlineText={t('Recommendations.instagramAccount')}
                  selectedIntegration={selectedIntegrations.items.find(
                    (x) =>
                      x.asset_class === AssetClass.profile && x.platform === Platform.instagram,
                  )}
                  selectText={t('Recommendations.campaign.selectInstagramAccount')}
                  disabled={effectivelyDisabled}
                  accountType="instagram_profile"
                />
              </AccordionCard>
            )}

            <AccordionCard
              title={t('Recommendations.socialAdvertAccountTitle')}
              wrapperClassName="select-ad-platform_section"
            >
              {showMeta && (
                <PlatformSelectMenu
                  handleClick={(_, id) => onIntegrationClicked(id)}
                  removeInteg={(e, id) => onIntegrationRemoved(e, id)}
                  integrations={partnerAccounts.filter(
                    (x) =>
                      x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
                  )}
                  viableIntegrations={partnerAccounts.filter(
                    (x) =>
                      x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
                  )}
                  subHeadlineText={t('Recommendations.campaign.metaAdAccountLabel')}
                  required
                  selectedIntegration={selectedAdAccount}
                  selectText={t('Recommendations.campaign.selectAdAccount')}
                  disabled={effectivelyDisabled}
                  accountType="facebook_ad_account"
                />
              )}
              {showGoogle && (
                <PlatformSelectMenu
                  handleClick={(_, id) => onIntegrationClicked(id)}
                  removeInteg={(e, id) => onIntegrationRemoved(e, id)}
                  integrations={partnerAccounts.filter(
                    (x) =>
                      x.asset_class === AssetClass.ad_account && x.platform === Platform.google,
                  )}
                  viableIntegrations={partnerAccounts.filter(
                    (x) =>
                      x.asset_class === AssetClass.ad_account && x.platform === Platform.google,
                  )}
                  subHeadlineText={t('Recommendations.campaign.googleAdAccountLabel')}
                  required
                  selectedIntegration={selectedGoogleAdAccount}
                  selectText={t('Recommendations.campaign.selectGoogleAdAccount')}
                  disabled={effectivelyDisabled}
                  loadingValue={!initialAccountsValidated}
                  accountType="google_ad_account"
                />
              )}
              {selectionError && (
                <ErrorMessage danger>{translateSetupError(t, selectionError)}</ErrorMessage>
              )}
            </AccordionCard>

            {(showMeta || showGoogle) && (
              <AccordionCard
                title={t('Recommendations.chooseResult')}
                wrapperClassName="select-ad-platform_section"
                className="position-relative"
              >
                {showMeta && (
                  <div>
                    <div className="platform-label-wrapper" style={{ marginBottom: '12px' }}>
                      <InfoBox
                        title={t('Tips.Recommendations.LeadForm.title')}
                        body={
                          <div className="d-flex flex-column gap-1">
                            <span>{t('Tips.Recommendations.LeadForm.body1')}</span>
                            <span>
                              <Trans
                                i18nKey="Tips.Recommendations.LeadForm.body2"
                                components={{
                                  a: (
                                    <a
                                      href={leadSupportArticleUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#2F80ED', display: 'inline-block' }}
                                    >
                                      support article
                                    </a>
                                  ),
                                }}
                              />
                            </span>
                          </div>
                        }
                      >
                        <label className="select-ad-platform_select-menu_select-label d-inline-block mb-0 mt-0">
                          {t('Tips.Recommendations.LeadForm.title')}
                        </label>
                      </InfoBox>

                      <PlatformLabel platform={'meta'} bothSelected={showMeta && showGoogle} />
                    </div>

                    <SearchDropdown
                      placeholder={t('Recommendations.campaign.searchLeadForms')}
                      inputPlaceholder={t('Recommendations.campaign.leadFormPlaceholder')}
                      options={leadForms.items.map((x) => ({
                        id: x.id,
                        name: `${x.name} (ID: ${x.id})`,
                      }))}
                      value={
                        leadFormValue
                          ? {
                              ...leadFormValue,
                              name: `${leadFormValue?.name} (ID: ${leadFormValue?.id})`,
                            }
                          : null
                      }
                      onChange={(item) => onLeadFormChanged(item?.id)}
                      removeSelectedItem={() => onLeadFormChanged('')}
                      onErrorBtnClick={retrieveLeadForms}
                      error={leadForms.error ? getErrorText(leadForms.message, 'leadForm') : null}
                      loading={leadForms.loading}
                      loadingValue={isSavingLeadForm}
                      disabled={effectivelyDisabled || goal !== CampaignGoal.utilization}
                    />

                    <div className="platform-label-wrapper mt-2" style={{ marginBottom: '12px' }}>
                      <label className="select-ad-platform_select-menu_select-label d-inline-block mb-0 mt-0">
                        {t('Recommendations.pixel')}
                      </label>

                      <PlatformLabel platform={'meta'} bothSelected={showMeta && showGoogle} />
                    </div>

                    <PlatformSelectMenu
                      handleClick={(_, id) => onIntegrationClicked(id)}
                      removeInteg={(e, id) => onIntegrationRemoved(e, id)}
                      integrations={partnerAccounts.filter(
                        (x) =>
                          x.asset_class === AssetClass.pixel && x.platform === Platform.facebook,
                      )}
                      viableIntegrations={getAvailablePixels(
                        partnerAccounts,
                        selectedAdAccount,
                        metaConnectionStatus,
                      )}
                      subHeadlineText={null}
                      selectedIntegration={selectedIntegrations.items.find(
                        (x) =>
                          x.asset_class === AssetClass.pixel && x.platform === Platform.facebook,
                      )}
                      selectText={t('Recommendations.campaign.selectPixelAccount')}
                      required={false}
                      disabled={effectivelyDisabled}
                      accountType="facebook_pixel"
                    />

                    {isPixel && (
                      <div className="conversion-container">
                        <div className="arrow-container">
                          <div className="arrow-lines" />
                          <div className="arrow-head" />
                        </div>
                        <InfoBox content="Tips.Recommendations.ConversionGoal">
                          <label className="select-ad-platform_select-menu_select-label d-inline-block mb-3">
                            {t('Tips.Recommendations.ConversionGoal.title')}
                          </label>
                        </InfoBox>
                        <SearchDropdown
                          placeholder={t('Recommendations.campaign.searchConversionGoals')}
                          options={conversionOptions}
                          value={conversionValue}
                          onChange={(item) => {
                            if (item.type === 'custom_event') {
                              onCustomEventTypeChanged(item.id)
                            } else {
                              onConversionGoalChanged(item?.id)
                            }
                          }}
                          removeSelectedItem={() => onConversionGoalChanged('')}
                          onErrorBtnClick={retrieveConversionGoals}
                          error={
                            conversionGoals.error ? getErrorText(conversionGoals.message) : null
                          }
                          loading={conversionGoals.loading}
                          loadingValue={isSavingConversionGoal}
                          disabled={effectivelyDisabled}
                          classNames="conversion-goal-select"
                          required
                        />
                      </div>
                    )}
                  </div>
                )}

                {showGoogle && (
                  <div>
                    <div className="platform-label-wrapper" style={{ marginBottom: '12px' }}>
                      <InfoBox content="Tips.Recommendations.GoogleConversionGoal">
                        <label className="select-ad-platform_select-menu_select-label d-inline-block mb-0 mt-0">
                          {t('Recommendations.campaign.googleConversionGoal.title')}
                        </label>
                      </InfoBox>

                      <PlatformLabel platform={'google'} bothSelected={showMeta && showGoogle} />
                    </div>

                    <GoogleConversionGoalPicker
                      customerId={selectedGoogleAdAccount?.asset_id}
                      disabled={effectivelyDisabled}
                      campaignType={googleConversionsCampaignType}
                      biddingStrategy={effectiveBiddingStrategy}
                      hasConversionAction={hasConversionAction}
                      onSelectionChange={onGoogleConversionGoalChanged}
                      onMetaChange={onGoogleConversionMetaChanged}
                      initialItemIds={googleConversionGoalItemIds}
                    />
                  </div>
                )}
              </AccordionCard>
            )}

            {showGoogle && (
              <AccordionCard
                title={t('Recommendations.campaignSettingsTitle')}
                wrapperClassName="select-ad-platform_section"
              >
                <div className="strategy-container">
                  <div>
                    <div className="platform-label-wrapper" style={{ marginBottom: '12px' }}>
                      <InfoBox content="Tips.Recommendations.GoogleBiddingStrategy">
                        <label className="select-ad-platform_select-menu_select-label d-inline-block mt-0 mb-0">
                          {t('Recommendations.campaign.googleBiddingStrategy.title')}
                        </label>
                      </InfoBox>

                      <PlatformLabel platform={'google'} bothSelected={showMeta && showGoogle} />
                    </div>

                    <SearchDropdown
                      placeholder={t('Recommendations.campaign.googleBiddingStrategy.title')}
                      options={biddingStrategyOptions}
                      value={biddingStrategyValue}
                      onChange={(item) => onBiddingStrategyChanged(item?.id)}
                      disabled={effectivelyDisabled}
                      loadingValue={isBiddingStrategyLoading}
                      classNames="google-bidding-strategy-select"
                      required
                      platform="google"
                    />
                  </div>

                  {!isBiddingStrategyLoading &&
                    targetValueField === BiddingTargetValueField.cpmBidMicros && (
                      <div>
                        <div className="platform-label-wrapper" style={{ marginBottom: '12px' }}>
                          <InfoBox content="Tips.Recommendations.GoogleCpmBid">
                            <label className="select-ad-platform_select-menu_select-label d-inline-block mt-0 mb-0">
                              {t('Recommendations.campaign.googleBiddingStrategy.cpmBidTitle')}
                            </label>
                          </InfoBox>

                          <PlatformLabel
                            platform={'google'}
                            bothSelected={showMeta && showGoogle}
                          />
                        </div>

                        <BiddingTargetValueInput
                          value={targetValueDisplayValue}
                          onChange={onTargetValueChanged}
                          disabled={effectivelyDisabled}
                          placeholder={t(
                            'Recommendations.campaign.googleBiddingStrategy.cpmBidPlaceholder',
                          )}
                        />
                      </div>
                    )}
                  {!isBiddingStrategyLoading &&
                    targetValueField === BiddingTargetValueField.cpcBidCeilingMicros && (
                      <div>
                        <div className="platform-label-wrapper" style={{ marginBottom: '12px' }}>
                          <InfoBox content="Tips.Recommendations.GoogleCpcBidCeiling">
                            <label className="select-ad-platform_select-menu_select-label d-inline-block mt-0 mb-0">
                              {t(
                                'Recommendations.campaign.googleBiddingStrategy.cpcBidCeilingTitle',
                              )}
                            </label>
                          </InfoBox>

                          <PlatformLabel
                            platform={'google'}
                            bothSelected={showMeta && showGoogle}
                          />
                        </div>

                        <BiddingTargetValueInput
                          value={targetValueDisplayValue}
                          onChange={onTargetValueChanged}
                          disabled={effectivelyDisabled}
                          placeholder={t(
                            'Recommendations.campaign.googleBiddingStrategy.cpcBidCeilingPlaceholder',
                          )}
                        />
                      </div>
                    )}
                  {!isBiddingStrategyLoading &&
                    targetValueField === BiddingTargetValueField.targetRoas && (
                      <div>
                        <div className="platform-label-wrapper" style={{ marginBottom: '12px' }}>
                          <InfoBox content="Tips.Recommendations.GoogleTargetRoas">
                            <label className="select-ad-platform_select-menu_select-label d-inline-block mt-0 mb-0">
                              {t('Recommendations.campaign.googleBiddingStrategy.targetRoasTitle')}
                            </label>
                          </InfoBox>
                          <PlatformLabel
                            platform={'google'}
                            bothSelected={showMeta && showGoogle}
                          />
                        </div>
                        <BiddingTargetValueInput
                          value={targetValueDisplayValue}
                          onChange={onTargetValueChanged}
                          disabled={effectivelyDisabled}
                          invalid={isTargetRoasMissing}
                          suffix="%"
                          placeholder={t(
                            'Recommendations.campaign.googleBiddingStrategy.targetRoasPlaceholder',
                          )}
                        />
                      </div>
                    )}
                </div>
              </AccordionCard>
            )}
          </form>
        </div>
      )}
    </div>
  )
}

SelectAdPlatformForm.propTypes = {
  isDisabled: PropTypes.bool,
}

function updateSelectedIntegrations(
  selectedIntegration,
  partnerAccounts,
  selectedIntegrations,
  metaConnectionStatus,
) {
  let newIntegrations = [
    ...selectedIntegrations.filter(
      (x) =>
        x.asset_class !== selectedIntegration.asset_class ||
        x.platform !== selectedIntegration.platform,
    ),
    selectedIntegration,
  ]

  const selectedAdAccount = newIntegrations.find((x) => x.asset_class === AssetClass.ad_account)

  let selectedPage = newIntegrations.find(
    (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
  )
  if (selectedPage) {
    const availablePages = getAvailablePages(
      partnerAccounts,
      selectedAdAccount,
      metaConnectionStatus,
    )
    if (!availablePages.some((x) => x.asset_id === selectedPage.asset_id)) {
      selectedPage = null
      newIntegrations = newIntegrations.filter(
        (x) => x.asset_class !== AssetClass.profile || x.platform !== Platform.facebook,
      )
      if (availablePages.length > 0) {
        selectedPage = availablePages[0]
        newIntegrations.push(selectedPage)
      }
    }
  }

  if (selectedIntegration.asset_class === AssetClass.ad_account) {
    const selectedPixel = newIntegrations.find((x) => x.asset_class === AssetClass.pixel)
    if (selectedPixel) {
      const availablePixels = getAvailablePixels(
        partnerAccounts,
        selectedAdAccount,
        metaConnectionStatus,
      )
      if (!availablePixels.some((x) => x.asset_id === selectedPixel.asset_id)) {
        newIntegrations = newIntegrations.filter((x) => x.asset_class !== AssetClass.pixel)
        if (availablePixels.length > 0) {
          newIntegrations.push(availablePixels[0])
        }
      }
    }
  }

  if (
    selectedIntegration.asset_class === AssetClass.ad_account ||
    selectedIntegration.asset_class === AssetClass.profile
  ) {
    const selectedInstagram = newIntegrations.find(
      (x) => x.asset_class === AssetClass.profile && x.platform === Platform.instagram,
    )
    if (selectedInstagram) {
      const availableInstagram = getAvailableInstagramAccounts(
        partnerAccounts,
        selectedAdAccount,
        selectedPage,
        metaConnectionStatus,
      )
      if (!availableInstagram.some((x) => x.asset_id === selectedInstagram.asset_id)) {
        newIntegrations = newIntegrations.filter(
          (x) => x.asset_class !== AssetClass.profile || x.platform !== Platform.instagram,
        )
        if (availableInstagram.length > 0) {
          newIntegrations.push(availableInstagram[0])
        }
      }
    }
  }

  return newIntegrations
}

export function getDefaultGoogleAdAccount(accounts, selectedAccounts, platform) {
  if (!platformIncludesGoogle(platform)) return undefined

  return (
    selectedAccounts.find(
      (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.google,
    ) ??
    accounts.find(
      (x) =>
        x.asset_class === AssetClass.ad_account && x.platform === Platform.google && x.is_active,
    )
  )
}

export function getDefaultIntegrations(
  accounts,
  selectedAccounts,
  metaConnectionStatus,
  platform = DEFAULT_SETUP_PLATFORM,
) {
  const googleAdAccount = getDefaultGoogleAdAccount(accounts, selectedAccounts, platform)

  if (!platformIncludesMeta(platform)) {
    return {
      adAccount: undefined,
      page: undefined,
      instagramAccount: undefined,
      pixel: undefined,
      googleAdAccount,
    }
  }

  if (!metaConnectionStatus) {
    // Don't enforce asset relationship validation for clients without meta connection.
    let adAccount = selectedAccounts.find(
      (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
    )
    if (!adAccount) {
      adAccount = accounts.find(
        (x) =>
          x.asset_class === AssetClass.ad_account &&
          x.platform === Platform.facebook &&
          x.is_active,
      )
    }

    let page = selectedAccounts.find(
      (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
    )
    if (!page) {
      page = accounts.find(
        (x) =>
          x.asset_class === AssetClass.profile && x.platform === Platform.facebook && x.is_active,
      )
    }

    const selectedInstagram = selectedAccounts.find(
      (x) => x.asset_class === AssetClass.profile && x.platform === Platform.instagram,
    )
    const selectedPixel = selectedAccounts.find((x) => x.asset_class === AssetClass.pixel)

    return {
      adAccount,
      page,
      instagramAccount: selectedInstagram,
      pixel: selectedPixel,
      googleAdAccount,
    }
  }

  const selectedAdAccount =
    selectedAccounts.find(
      (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
    ) ??
    accounts.find(
      (x) =>
        x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook && x.is_active,
    )
  const availablePages = getAvailablePages(accounts, selectedAdAccount, metaConnectionStatus)
  let selectedPage = selectedAccounts.find(
    (x) => x.asset_class === AssetClass.profile && x.platform === Platform.facebook,
  )

  if (availablePages.length > 0 && !availablePages.some((x) => x.id === selectedPage?.id)) {
    selectedPage = availablePages[0]
  } else {
    selectedPage = null
  }

  const selectedPixel = selectedAccounts.find((x) => x.asset_class === AssetClass.pixel)
  const selectedInstagram = selectedAccounts.find(
    (x) => x.asset_class === AssetClass.profile && x.platform === Platform.instagram,
  )

  return {
    adAccount: selectedAdAccount,
    page: selectedPage,
    instagramAccount: selectedInstagram,
    pixel: selectedPixel,
    googleAdAccount,
  }
}

const getDefaultCustomEventType = (leadFormId) => {
  return leadFormId ? 'LEAD' : 'PURCHASE'
}

export default SelectAdPlatformForm
